import { create } from "zustand";
import type { MarketplaceListing } from "../models/apiTypes";

/**
 * Discriminated set of MapCanvas modals/popups. Only one is ever meant to be
 * visually open at a time (verified against MapCanvas.tsx: every open-call site
 * is an if/else-if branch or immediately follows a close of the previous modal —
 * no code path relies on two of these being true simultaneously).
 */
export type MapModalName =
    | 'realEstate'
    | 'realEstateInfo'
    | 'mapObject'
    | 'plotFree'
    | 'plotTaken'
    | 'newIsland'
    | 'dailyQuests'
    | 'mapOnboarding'
    | 'typeSelection'
    | 'salePreview'
    | 'winstonCat'
    | 'lighthouse'
    | 'navConfirm'
    | 'mapWelcome'
    | 'tutorialReward';

export type NavConfirmDestination = 'shop' | 'hall_of_fame' | 'city_hall';

/**
 * Per-modal payload data. Kept as one flat bag (not a per-modal-name variant)
 * because a few fields legitimately outlive a modal switch:
 * `plotFreeOnboarding` / `openPlotFreeAfterReward` are written while
 * `tutorialReward` is the active modal and consumed after the switch to
 * `plotFree` — see handleTutorialRewardDone-style handlers in MapCanvas.
 */
export interface MapModalPayload {
    /** map_object_id ділянки для каталогу будинків (RealEstateModal). Фіксується при
     * відкритті, не залежить від selectedMapObject (який може скинутися sync-ом). */
    realEstateCatalogPlotId: number | null;
    /** RealEstateModal відкрита як частина onboarding-флоу. */
    realEstateOnboarding: boolean;
    /** PlotFreeModal відкрита (або буде відкрита, після tutorialReward) як частина onboarding-флоу. */
    plotFreeOnboarding: boolean;
    /** Відкладений намір: встановлюється поки активна tutorialReward — на її закритті відкрити PlotFreeModal. */
    openPlotFreeAfterReward: boolean;
    /** Світова позиція для MapObjectTypeSelectionModal → admin plot creation. */
    pendingMapObjectCreation: { x: number; y: number } | null;
    /** Лістинг, який показує SalePreviewModal. */
    salePreviewListing: MarketplaceListing | null;
    /** Куди веде NavigationConfirmModal. */
    navConfirmDestination: NavConfirmDestination;
    /** initialScreen для MapStaticObjectModal, коли відкрита з election-vote флоу. */
    electionCityHallScreen: import('../screens/cityHall/types').ScreenType | null;
}

const defaultPayload: MapModalPayload = {
    realEstateCatalogPlotId: null,
    realEstateOnboarding: false,
    plotFreeOnboarding: false,
    openPlotFreeAfterReward: false,
    pendingMapObjectCreation: null,
    salePreviewListing: null,
    navConfirmDestination: 'shop',
    electionCityHallScreen: null,
};

interface MapModalStore {
    activeModal: MapModalName | null;
    payload: MapModalPayload;
    /** Open a modal, optionally merging payload fields (fields not present in the patch keep their current value). */
    openModal: (modal: MapModalName, payload?: Partial<MapModalPayload>) => void;
    /**
     * Close whichever modal is open (activeModal → null). Does NOT clear payload —
     * callers that need to reset specific payload fields on close (e.g. MapCanvas's
     * handleCloseModal, which used to also clear realEstateCatalogPlotId etc.) do so
     * explicitly via the setters below.
     */
    closeModal: () => void;
    setRealEstateCatalogPlotId: (id: number | null) => void;
    setPlotFreeOnboarding: (value: boolean) => void;
    setOpenPlotFreeAfterReward: (value: boolean) => void;
    setPendingMapObjectCreation: (value: { x: number; y: number } | null) => void;
    setSalePreviewListing: (value: MarketplaceListing | null) => void;
    setElectionCityHallScreen: (value: import('../screens/cityHall/types').ScreenType | null) => void;
}

const useMapModalStore = create<MapModalStore>((set) => ({
    activeModal: null,
    payload: defaultPayload,
    openModal: (modal, payload) => set((state) => ({
        activeModal: modal,
        payload: payload ? { ...state.payload, ...payload } : state.payload,
    })),
    closeModal: () => set({ activeModal: null }),
    setRealEstateCatalogPlotId: (id) =>
        set((state) => ({ payload: { ...state.payload, realEstateCatalogPlotId: id } })),
    setPlotFreeOnboarding: (value) =>
        set((state) => ({ payload: { ...state.payload, plotFreeOnboarding: value } })),
    setOpenPlotFreeAfterReward: (value) =>
        set((state) => ({ payload: { ...state.payload, openPlotFreeAfterReward: value } })),
    setPendingMapObjectCreation: (value) =>
        set((state) => ({ payload: { ...state.payload, pendingMapObjectCreation: value } })),
    setSalePreviewListing: (value) =>
        set((state) => ({ payload: { ...state.payload, salePreviewListing: value } })),
    setElectionCityHallScreen: (value) =>
        set((state) => ({ payload: { ...state.payload, electionCityHallScreen: value } })),
}));

export default useMapModalStore;
